import React, { FC } from 'react';
import * as Yup from "yup";
import {Controller, useForm} from "react-hook-form";
import { Password } from 'primereact/password';
import {yupResolver} from "@hookform/resolvers/yup";
import styles from "../authentication.module.scss";
import FieldWrapper from "../../../uiComponents/FieldWrapper/FieldWrapper";
import {InputText} from "primereact/inputtext";
import ButtonPrimary from "../../../uiComponents/ButtonPrimary/ButtonPrimary";

export type TFieldName = 'code' | 'email' | 'password' | 'confirmPassword';
export type TFieldConfig = {
    fieldName: TFieldName;
    label: string;
    placeholder: string;
    readonly?: boolean;
    type: 'text' | 'password';
};

export type TSetNewPasswordValues = Record<TFieldName, string>;

const fieldsConfig: TFieldConfig[] = [
    {
        fieldName: 'code',
        label: 'Verification code',
        placeholder: 'Enter verification code',
        type: 'text',
    },
    {
        fieldName: 'email',
        label: 'Email',
        placeholder: 'example@gmail.com',
        readonly: true,
        type: 'text',
    },
    {
        fieldName: 'password',
        label: 'New password',
        placeholder: '',
        type: 'password',
    },
    {
        fieldName: 'confirmPassword',
        label: 'Confirm new password',
        placeholder: '',
        type: 'password',
    }
];

interface TSignUpFormProps {
    onSubmit: (values: TSetNewPasswordValues) => void;
    isLoading: boolean;
    email: string;
}

const SetNewPasswordForm: FC<TSignUpFormProps> = ({ onSubmit, isLoading, email }) => {
    const formSchema = Yup.object().shape({
        code: Yup.string().required('Verification code is required'),
        email: Yup.string().email('Invalid email').required('Email is required'),
        password: Yup.string().required('Password is required'),
        confirmPassword: Yup.string().required('Confirm password is required'),
    });
    const initialFormValues = {
        code: '',
        email: email,
        password: '',
        confirmPassword: '',
    };
    const form = useForm({
        resolver: yupResolver(formSchema),
        defaultValues: initialFormValues,
    });
    const { control, handleSubmit, watch } = form;
    const formErrors = form.formState.errors;
    const currentValues = watch();
    const { password, confirmPassword } = currentValues;
    return (
        <form className={styles.authForm} onSubmit={handleSubmit(onSubmit)}>
            <p className={styles.headTxt}>Reset your password</p>
            <p className="text-base mt-2">
            If a user exists with this email address, you will receive a verification code in a moment. 
             If a verification code doesn’t come through, try signing up instead.
            </p>
            <div className={styles.fields}>
                {fieldsConfig.map((fieldConfig: TFieldConfig) => {
                    const {fieldName, placeholder, label, readonly, type} = fieldConfig;
                    const fieldError = formErrors[fieldName]?.message;
                    const isEmailField = fieldName === 'email';
                    const isPasswordField = fieldName === 'password' || fieldName === 'confirmPassword';
                    if (type === 'password') {
                        return (
                            <div className={styles.fieldItem} key={fieldName}>
                                <Controller
                                    name={fieldName as TFieldName}
                                    control={control}
                                    render={({field}) => {
                                        return (
                                            <FieldWrapper
                                                className={styles.fieldWide}
                                                errorMessage={fieldError}
                                                label={label}
                                                required
                                            >
                                                <Password
                                                    className={styles.fieldWide}
                                                    value={field.value as string}
                                                    name={fieldName}
                                                    placeholder={placeholder}
                                                    onChange={(e) => {
                                                        field.onChange(e.target.value);
                                                    }}
                                                    autoComplete="new-password"
                                                    feedback={false}
                                                    toggleMask
                                                />
                                            </FieldWrapper>
                                        );
                                    }}
                                />
                            </div>
                        );
                    }
                    return (
                        <div className={styles.fieldItem} key={fieldName}>
                            <Controller
                                name={fieldName as TFieldName}
                                control={control}
                                render={({field}) => {
                                    return (
                                        <FieldWrapper
                                            className={styles.fieldWide}
                                            errorMessage={fieldError}
                                            label={label}
                                            required
                                        >
                                            <InputText
                                                readOnly={readonly}
                                                className={styles.fieldWide}
                                                value={field.value as string}
                                                type={isPasswordField ? 'password' : 'text'}
                                                name={fieldName}
                                                placeholder={placeholder}
                                                onChange={(e) => {
                                                    field.onChange(e.target.value);
                                                }}
                                                {...(isPasswordField || isEmailField ? { autoComplete: 'new-password' } : {})}
                                            />
                                        </FieldWrapper>
                                    );
                                }}
                            />
                        </div>
                    );
                })}
            </div>
            <ButtonPrimary
                label="Change password"
                className={styles.actionBtn}
                type="submit"
                disabled={password !== confirmPassword || isLoading}
            />
        </form>
    )
}

export default SetNewPasswordForm;
